import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, Check } from 'lucide-react';
import colors from '../../config/colors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AutocompleteQuestion = ({ question, value, onChange }) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch schools on mount
  useEffect(() => {
    const fetchSchools = async () => {
      setLoading(true);
      try {
        // Aggressive cache-busting
        const timestamp = new Date().getTime();
        const response = await axios.get(`${BACKEND_URL}${question.apiEndpoint}?t=${timestamp}&nocache=${Math.random()}`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        // CRITICAL DEBUGGING
        console.log('=== SCHOOL FETCH DEBUG ===');
        console.log('Full Response:', response);
        console.log('Response Data:', response.data);
        console.log('Total Count from API:', response.data.total_count);
        console.log('Schools Array Length:', response.data.schools?.length || 0);
        console.log('Debug Info:', response.data.debug_info);
        console.log('API URL called:', `${BACKEND_URL}${question.apiEndpoint}?t=${timestamp}`);
        console.log('Response Status:', response.status);
        console.log('Response Headers:', response.headers);
        console.log('==========================');
        
        const schoolList = response.data.schools.map(school => ({
          id: school.id,
          name: school.full_name,  // Use full_name as primary
          fullName: school.full_name,
          commonName: school.name  // Keep common_name for reference
        }));
        setSchools(schoolList);
        console.log('Schools set in state:', schoolList.length);
        console.log('Sample schools:', schoolList.slice(0, 3).map(s => s.name));
      } catch (error) {
        console.error('Error fetching schools:', error);
        // Fallback: allow manual entry
        setSchools([]);
      } finally {
        setLoading(false);
      }
    };

    if (question.apiEndpoint) {
      fetchSchools();
    }
  }, [question.apiEndpoint, BACKEND_URL]);

  // Filter schools based on input
  useEffect(() => {
    if (inputValue.length === 0) {
      // Show ALL schools when input is empty (so users can browse full list)
      setFilteredSchools(schools);
      return;
    }

    const searchTerm = inputValue.toLowerCase();
    const matches = schools.filter(school => 
      school.name.toLowerCase().includes(searchTerm) ||
      school.commonName?.toLowerCase().includes(searchTerm)
    );

    setFilteredSchools(matches);
    setIsOpen(matches.length > 0 && document.activeElement === inputRef.current);
    setSelectedIndex(-1);
  }, [inputValue, schools, inputRef]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  // Handle school selection
  const handleSelectSchool = (school) => {
    setInputValue(school.name);
    onChange(school.name);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredSchools.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredSchools.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSelectSchool(filteredSchools[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  return (
    <div style={{ padding: '12px 0' }}>
      <h2 style={{
        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
        fontWeight: '700',
        color: colors.textPrimary,
        marginBottom: '8px',
        lineHeight: '1.2'
      }}>
        {question.question}
      </h2>

      {question.subtitle && (
        <p style={{
          fontSize: '1rem',
          color: colors.textSecondary,
          marginBottom: '20px'
        }}>
          {question.subtitle}
        </p>
      )}
      
      {/* Helper text for autocomplete */}
      {schools.length > 0 && !loading && (
        <p style={{
          fontSize: '0.85rem',
          color: colors.textMuted,
          marginBottom: '12px',
          fontStyle: 'italic'
        }}>
          💡 All {schools.length} schools are available. Click the field to browse all, or start typing to search.
        </p>
      )}

      <div style={{ position: 'relative' }} ref={dropdownRef}>
        {/* Input Field */}
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              // Show all schools when focused (even if input is empty)
              if (inputValue.length === 0) {
                setFilteredSchools(schools);
              }
              if (filteredSchools.length > 0 || schools.length > 0) {
                setIsOpen(true);
              }
            }}
            placeholder={question.placeholder}
            style={{
              width: '100%',
              padding: '18px 50px 18px 20px',
              fontSize: '1.1rem',
              border: `2px solid ${isOpen ? colors.brandPrimary : colors.borderMedium}`,
              borderRadius: '12px',
              background: colors.bgCard,
              color: colors.textPrimary,
              outline: 'none',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit'
            }}
          />
          
          {/* Search Icon */}
          <div style={{
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: colors.textMuted,
            pointerEvents: 'none'
          }}>
            <Search size={20} />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{
            marginTop: '8px',
            fontSize: '0.85rem',
            color: colors.textMuted,
            fontStyle: 'italic'
          }}>
            Loading schools...
          </div>
        )}

        {/* Dropdown */}
        {isOpen && filteredSchools.length > 0 && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            background: colors.bgCard,
            border: `2px solid ${colors.brandPrimary}`,
            borderRadius: '12px',
            maxHeight: '400px',
            overflowY: 'auto',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            zIndex: 1000
          }}>
            {/* Show count when showing all schools */}
            {inputValue.length === 0 && filteredSchools.length > 10 && (
              <div style={{
                padding: '12px 16px',
                background: colors.bgAccent,
                borderBottom: `1px solid ${colors.borderLight}`,
                fontSize: '0.85rem',
                color: colors.textMuted,
                fontWeight: '600'
              }}>
                Showing all {filteredSchools.length} schools - start typing to filter
              </div>
            )}
            {filteredSchools.map((school, index) => (
              <button
                key={school.id}
                onClick={() => handleSelectSchool(school)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  textAlign: 'left',
                  background: selectedIndex === index ? colors.bgAccent : 'transparent',
                  border: 'none',
                  borderBottom: index < filteredSchools.length - 1 ? `1px solid ${colors.borderLight}` : 'none',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px'
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div>
                  <div style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: colors.textPrimary,
                    marginBottom: '2px'
                  }}>
                    {school.name}
                  </div>
                </div>
                
                {inputValue.toLowerCase() === school.name.toLowerCase() && (
                  <Check size={18} color={colors.brandPrimary} strokeWidth={3} />
                )}
              </button>
            ))}
          </div>
        )}

        {/* No Results */}
        {isOpen && filteredSchools.length === 0 && inputValue.length > 0 && !loading && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '8px',
            background: colors.bgCard,
            border: `2px solid ${colors.borderMedium}`,
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            zIndex: 1000
          }}>
            <div style={{
              fontSize: '0.95rem',
              color: colors.textMuted,
              textAlign: 'center'
            }}>
              No schools found. You can continue typing to enter manually.
            </div>
          </div>
        )}

        {/* Helper Text */}
        {!isOpen && inputValue.length > 0 && (
          <div style={{
            marginTop: '8px',
            fontSize: '0.85rem',
            color: colors.textMuted
          }}>
            {filteredSchools.length > 0 ? (
              <>✓ School name entered</>
            ) : (
              <>Custom school name (not in our database)</>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutocompleteQuestion;
